import React, { useState, useRef, useCallback } from 'react';
import AsyncSelect from 'react-select/async';
import AccountsUsersService from '../../../services/AccountsUsersService';
import { theme } from '../../../theme';

/**
 * Remote account search for the user form.
 * Queries the backend as the user types (debounced), never loads all accounts.
 */
export function UserAccountSearch({ value, onChange, onBlur, placeholder = 'Buscar cuenta...' }) {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const debounceRef = useRef(null);

    const loadOptions = useCallback((input, callback) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setError('');
        debounceRef.current = setTimeout(async () => {
            setIsLoading(true);
            try {
                const results = await AccountsUsersService.searchAccounts({ search: input || '', limit: 20 });
                callback(results.map((r) => ({
                    label: r.label,
                    value: r.pub_name,
                    prefix: r.prefix,
                    pub_name: r.pub_name,
                })));
            } catch (e) {
                setError('No se pudo cargar el buscador de cuentas');
                callback([]);
            } finally {
                setIsLoading(false);
            }
        }, 400);
    }, []);

    const darkStyles = {
        control: (provided, state) => ({
            ...provided,
            backgroundColor: theme.surface2,
            borderColor: state.isFocused ? theme.primary : theme.border,
            color: theme.text,
            '&:hover': { borderColor: state.isFocused ? theme.primary : theme.borderStrong },
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? theme.bg : theme.surface2,
            color: theme.text,
            cursor: 'pointer',
            '&:active': { backgroundColor: theme.border },
        }),
        singleValue: (provided) => ({ ...provided, color: theme.text }),
        input: (provided) => ({ ...provided, color: theme.text }),
        placeholder: (provided) => ({ ...provided, color: theme.textDisabled }),
        menu: (provided) => ({ ...provided, backgroundColor: theme.surface2, border: `1px solid ${theme.border}` }),
        menuList: (provided) => ({ ...provided, maxHeight: 220 }),
    };

    return (
        <div>
            <AsyncSelect
                isClearable
                cacheOptions={false}
                defaultOptions
                loadOptions={loadOptions}
                isLoading={isLoading}
                value={value || null}
                onChange={onChange}
                onBlur={onBlur}
                placeholder={placeholder}
                styles={darkStyles}
                theme={(themeObj) => ({
                    ...themeObj,
                    colors: { ...themeObj.colors, primary: theme.primary },
                })}
            />
            {error && <small className="text-danger d-block mt-1">{error}</small>}
        </div>
    );
}

export default UserAccountSearch;